import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCan } from "../../auth/AuthProvider";
import { AttachDatabaseModal } from "../../components/AttachDatabaseModal";
import { ConnectionDetails } from "../../components/ConnectionDetails";
import { DatabaseAccess } from "../../components/DatabaseAccess";
import { Modal } from "../../components/Modal";
import { Alert, Button, Card, CardHeader } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useDatabaseConnection, useDeleteDatabase } from "../../lib/queries";
import { useDatabaseTabContext } from "./context";

/*
 * Everything about reaching the database, in the order somebody asks: the
 * connection itself, who else can reach it, the allowlist, the credential,
 * and — sharing the tab for sharing the audience — deletion. The Connection
 * card lived on Overview until the two halves of one subject confused more
 * than the split saved; the password still hides behind a reveal, so moving
 * the card moved no secret onto anyone's screen.
 */
export function DatabaseAccessTab() {
  const { t } = useTranslation("database");
  const { orgId, database } = useDatabaseTabContext();
  const navigate = useNavigate();
  const canManage = useCan("developer");
  const canDelete = useCan("admin");
  const deleteDatabase = useDeleteDatabase(orgId ?? "");

  /* Fetch gated on the first reveal — the response carries the password, and
     it should not sit in memory for everyone who opens the tab. */
  const [needsConnection, setNeedsConnection] = useState(false);
  const connection = useDatabaseConnection(orgId, needsConnection ? database.id : null);
  const [attaching, setAttaching] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    try {
      const result = await deleteDatabase.mutateAsync(database.id);
      setConfirming(false);
      /*
       * The warning belongs on the list, not here — this page is about to stop
       * existing. Apps still holding a variable that points at the deleted
       * database will break on their next deploy, and that is worth carrying.
       */
      void navigate("/databases", {
        replace: true,
        state:
          result.stillReferencedBy.length > 0
            ? {
                notice: t("access.deletedStillReferenced", {
                  apps: result.stillReferencedBy.map((a) => a.name).join(", "),
                }),
              }
            : undefined,
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("access.deleteFailed"));
      setConfirming(false);
    }
  }

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="warning">{notice}</Alert>}

      <Card>
        <CardHeader
          title={t("access.connection")}
          action={
            canManage ? (
              <Button variant="secondary" size="sm" onClick={() => setAttaching(true)}>
                {t("access.attach")}
              </Button>
            ) : undefined
          }
        />
        <div className="space-y-4 p-5">
          <ConnectionDetails
            database={database}
            connection={connection.data}
            onNeedsConnection={() => setNeedsConnection(true)}
            loading={connection.isLoading}
            error={connection.error?.message ?? null}
            canReveal={canManage}
          />
          {/*
            The hostname already shown above, once high availability is on.
            Not a second connection string. Hidden while `ha` is null or the
            API omits it.
          */}
          {database.ha != null && (
            <p className="border border-line bg-canvas px-3 py-2.5 text-sm leading-6 text-fg-muted">
              {t("access.haHostname")}
            </p>
          )}
          {/*
            No "internal only, publish a port below" note. The panel below
            says the same thing twice over -- in its own description and in
            the note beside its button -- and it says it where the button is,
            which is the only place the advice can be acted on.
          */}
        </div>
      </Card>

      {/*
        No wrapper card: the component below renders one per decision, so
        nesting them inside an "Access" card put two headings on every section
        and left the page reading as one undifferentiated block.
      */}
      <DatabaseAccess orgId={orgId} database={database} canManage={canDelete} />

      {canDelete && (
        <Card className="border-danger/40">
          <CardHeader
            title={t("access.delete")}
            description={t("access.deleteDescription")}
          />
          <div className="p-5">
            <Button variant="danger" onClick={() => setConfirming(true)}>
              {t("access.deleteAction")}
                            </Button>
          </div>
        </Card>
      )}

      <AttachDatabaseModal
        orgId={orgId}
        database={attaching ? database : null}
        onClose={() => setAttaching(false)}
        onDone={(message) => {
          setAttaching(false);
          setNotice(message);
        }}
      />

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t("access.deleteTitle", { name: database.name })}
        description={t("access.deleteDescription")}
      >
        <div className="space-y-4">
          <Alert>{t("access.dumpsKept")}</Alert>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t("access.cancel")}
            </Button>
            <Button
              variant="danger"
              loading={deleteDatabase.isPending}
              onClick={() => void remove()}
            >
              {t("access.deletePermanently")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
